import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import emailjs from "@emailjs/browser";
import { alert } from "../utils/alert";

const Contact = () => {
  const form = useRef<HTMLFormElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);

  const faceImg = require("../assets/images/face.jpeg");
  const newicon = new (L.icon as any)({
    iconUrl: faceImg,
    iconSize: [56, 56],
    className: "rounded-circle border-gray",
  });

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    emailjs
      .sendForm(
        process.env.REACT_APP_EMAIL_JS_SERVICE_ID as string,
        process.env.REACT_APP_EMAIL_JS_TEMPLATE_ID as string,
        form.current as HTMLFormElement,
        process.env.REACT_APP_EMAIL_JS_PUBLIC_KEY as string,
      )
      .then(
        () => {
          setIsLoading(false);
          form.current?.reset();
          alert().fire({
            icon: "success",
            title: "Message sent successfully.",
          });
        },
        (error) => {
          setIsLoading(false);
          alert().fire({ icon: "error", title: error.message });
        },
      );
  };

  const fields = [
    {
      type: "email",
      name: "user_email",
      placeholder: "Your email",
      icon: "fa-regular fa-envelope",
    },
    {
      type: "text",
      name: "subject",
      placeholder: "Subject",
      icon: "fa-regular fa-pen-to-square",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

        .contact-section {
          min-height: 100vh;
          background: #0a0a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 24px;
          font-family: 'DM Sans', sans-serif;
          color: #f0f0f0;
          position: relative;
          overflow: hidden;
        }

        /* Grid bg */
        .contact-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        /* Orb */
        .contact-section::after {
          content: '';
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(74,222,128,0.06) 0%, transparent 70%);
          bottom: -150px;
          right: -100px;
          pointer-events: none;
        }

        .contact-inner {
          max-width: 1100px;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        /* Header */
        .contact-header {
          margin-bottom: 56px;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .contact-header.visible { opacity: 1; transform: translateY(0); }

        .contact-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #4ade80;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .contact-eyebrow::after {
          content: '';
          display: block;
          width: 40px;
          height: 1px;
          background: #4ade80;
          opacity: 0.6;
        }

        .contact-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(38px, 5.5vw, 68px);
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1;
          margin: 0;
        }
        .contact-title-accent {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .contact-subtitle {
          font-size: 15px;
          font-weight: 300;
          color: rgba(240,240,240,0.4);
          margin: 14px 0 0 0;
          max-width: 380px;
          line-height: 1.7;
        }

        /* Layout grid */
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 28px;
          align-items: start;
        }

        @media (max-width: 900px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }
          .contact-map-col { order: -1; }
        }

        /* Map */
        .contact-map-col {
          opacity: 0;
          transform: translateX(-24px);
          transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
        }
        .contact-map-col.visible { opacity: 1; transform: translateX(0); }

        .map-card {
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.07);
          height: 420px;
          position: relative;
        }

        .map-card .leaflet-container {
          height: 100%;
          width: 100%;
          background: #111118;
        }

        /* Location tag */
        .map-location-tag {
          position: absolute;
          bottom: 16px;
          left: 16px;
          z-index: 999;
          background: rgba(10,10,15,0.85);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(74,222,128,0.25);
          border-radius: 100px;
          padding: 7px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.05em;
          color: rgba(240,240,240,0.7);
          pointer-events: none;
        }
        .map-location-tag i { color: #4ade80; font-size: 11px; }

        /* Form */
        .contact-form-col {
          opacity: 0;
          transform: translateX(24px);
          transition: opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s;
        }
        .contact-form-col.visible { opacity: 1; transform: translateX(0); }

        .form-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          padding: 36px 32px;
        }

        .form-card-title {
          font-family: 'Syne', sans-serif;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 28px 0;
          color: rgba(240,240,240,0.9);
        }

        /* Input wrapper */
        .field-wrap {
          position: relative;
          margin-bottom: 16px;
        }

        .field-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 13px;
          color: rgba(240,240,240,0.25);
          pointer-events: none;
          transition: color 0.25s;
          z-index: 1;
        }

        .field-wrap.focused .field-icon { color: #4ade80; }

        .contact-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 13px 16px 13px 42px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: #f0f0f0;
          outline: none;
          transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
          box-sizing: border-box;
        }
        .contact-input::placeholder { color: rgba(240,240,240,0.25); }
        .contact-input:focus {
          border-color: rgba(74,222,128,0.45);
          background: rgba(74,222,128,0.04);
          box-shadow: 0 0 0 3px rgba(74,222,128,0.08);
        }

        .contact-textarea {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 13px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #f0f0f0;
          outline: none;
          resize: vertical;
          min-height: 120px;
          transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
          box-sizing: border-box;
          margin-bottom: 16px;
        }
        .contact-textarea::placeholder { color: rgba(240,240,240,0.25); }
        .contact-textarea:focus {
          border-color: rgba(74,222,128,0.45);
          background: rgba(74,222,128,0.04);
          box-shadow: 0 0 0 3px rgba(74,222,128,0.08);
        }

        .submit-btn {
          width: 100%;
          padding: 14px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          color: #0a0a0f;
          font-family: 'Syne', sans-serif;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
        }
        .submit-btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(74,222,128,0.25);
        }
        .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(10,10,15,0.3);
          border-top-color: #0a0a0f;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Divider */
        .contact-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, rgba(74,222,128,0.4), rgba(255,255,255,0.06) 50%, transparent);
          margin-bottom: 48px;
          opacity: 0;
          transition: opacity 0.6s ease 0.15s;
        }
        .contact-divider.visible { opacity: 1; }
      `}</style>

      <section className="contact-section" ref={sectionRef}>
        <div className="contact-inner">
          {/* Header */}
          <div className={`contact-header ${visible ? "visible" : ""}`}>
            <p className="contact-eyebrow">Get In Touch</p>
            <h2 className="contact-title">
              Let's <span className="contact-title-accent">Connect</span>
            </h2>
            <p className="contact-subtitle">
              Have a project in mind or just want to say hi? My inbox is always
              open.
            </p>
          </div>

          <div className={`contact-divider ${visible ? "visible" : ""}`} />

          {/* Two-column grid */}
          <div className="contact-grid">
            {/* Map */}
            <div className={`contact-map-col ${visible ? "visible" : ""}`}>
              <div className="map-card">
                <MapContainer
                  center={[50.85045, 4.34878]}
                  zoom={13}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[50.85045, 4.34878]} icon={newicon}>
                    <Popup>Hi, I'm based in Belgium.</Popup>
                  </Marker>
                </MapContainer>
                <div className="map-location-tag">
                  <i className="fa-solid fa-location-dot" />
                  Brussels, Belgium
                </div>
              </div>
            </div>

            {/* Form */}
            <div className={`contact-form-col ${visible ? "visible" : ""}`}>
              <div className="form-card">
                <p className="form-card-title">Send a message</p>

                <form ref={form} onSubmit={handleSubmit}>
                  {fields.map((f) => (
                    <div
                      key={f.name}
                      className={`field-wrap ${focused === f.name ? "focused" : ""}`}
                    >
                      <i className={`${f.icon} field-icon`} />
                      <input
                        type={f.type}
                        name={f.name}
                        placeholder={f.placeholder}
                        required
                        className="contact-input"
                        onFocus={() => setFocused(f.name)}
                        onBlur={() => setFocused(null)}
                      />
                    </div>
                  ))}

                  <textarea
                    name="message"
                    placeholder="Your message…"
                    rows={5}
                    required
                    className="contact-textarea"
                  />

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner" /> Sending…
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" /> Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
